import { useMsal } from '@azure/msal-react';
import { Button, Checkbox, InfoLabel, MessageBar, MessageBarActions, MessageBarBody, Radio, SearchBox, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Tooltip, useFocusableGroup } from '@fluentui/react-components';
import { ArrowDownload16Regular, ArrowDownloadRegular, ChevronDown12Regular, ChevronUp12Regular, GavelRegular, SparkleRegular, StarFilled } from '@fluentui/react-icons';
import { format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { observer } from 'mobx-react-lite';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { downloadFile } from '../../../common/file/download-file';
import { downloadJsonBlob } from '../../../common/file/download-json-blob';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { AI_SCRUB_OVERWRITE_WARNING } from '../../../common/scrubbing/ai-scrub-overwrite-warning';
import { canStartAiScrub } from '../../../common/scrubbing/can-start-ai-scrub';
import { hasPriorAgentScrub } from '../../../common/scrubbing/has-prior-agent-scrub';
import { areAllSelectedPassEligible, PASS_INELIGIBLE_TOOLTIP } from '../../../common/scrubbing/pass-eligibility';
import { scrubbingStateBadge } from '../../../common/scrubbing/scrubbing-state-badge';
import { scrubbingStatusClassNameColumn } from '../../../common/scrubbing/scrubbing-status-class-name';
import { NominationSummarySortKey } from '../../../common/sorting/nomination-summary-sort-key';
import { sortAwardNominationSummariesByKey } from '../../../common/sorting/sort-award-nomination-summary';
import { getUserNames } from '../../../common/string/get-user-names';
import { getTooltipContentByFieldName } from '../../../common/tooltip/tooltip-get-content';
import { AwardNominationSummary, blankNominationScrubbingStatus, NominationScrubbingStatus, ScrubbingStatusId } from '../../../models';
import { ResourceTypes } from '../../../models/resource-services';
import { RoutePaths } from '../../../route-paths';
import { BatchProgressIndicator } from '../../shared-components/ai-scrubbing/BatchProgressIndicator';
import { cancelOutcomeActions, cancelOutcomeMessage, cancelStatusCode, classifyCancelOutcome } from '../../shared-components/ai-scrubbing/cancel-outcome';
import { DisqualificationReasoningDialog } from '../../shared-components/ai-scrubbing/DisqualificationReasoningDialog';
import { ScoringReasoningDialog } from '../../shared-components/ai-scrubbing/ScoringReasoningDialog';
import { ExportNominationFieldsModal } from '../../shared-components/export-nominations-fields-modal/ExportNominationFieldsModal';
import { ExportNominationsModal, ExportType } from '../../shared-components/export-nominations-modal/ExportNominationsModal';
import { FlexBox } from '../../shared-components/flex-box';
import Loading from '../../shared-components/Loading';
import { AiScrubOverwriteConfirmModal, MultipleDisqualifyScrubbingModal, PassScrubbingModal } from '../../shared-components/scrubbing-modal';
import { clearPersistedBatchRunId, useAiScrubProgress } from '../hooks/use-ai-scrub-progress';
import { AiScrubbingTriggerDialog } from './AiScrubbingTriggerDialog';
import './AwardNominationSummaryTable.scss';
import { OverrideScrubbingModal } from './OverrideScrubbingModal';

interface AwardNominationSummaryTableProps {
  awardId: number;
  canAssignWinnersAndFinalists: boolean;
  setSelectedAwardNominationSummary(summary: AwardNominationSummary): void;
  downloadAwardExportedNominationsSummaries(awardId: number, query: string, selectedFields: string[]): Promise<void>;
}

const AwardNominationSummaryTableImpl: React.FC<AwardNominationSummaryTableProps> = (props) => {
  const {
    awardId,
    canAssignWinnersAndFinalists,
    downloadAwardExportedNominationsSummaries,
    setSelectedAwardNominationSummary,
  } = props;

  const { instance } = useMsal();

  const {
    apiStore,
    apiEndpointCallStatesStore,
    awardPageUiStore,
    awardStore,
    errorStore,
    nominationForStaffPageUiStore,
    userStore,
  } = useStores();

  const commonStyles = useCommonStyles();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const [showArrowOnKey, setShowArrowOnKey] = useState('');
  const [lastSortedKey, setLastSortedKey] = useState<string>('');
  const [sortedAwardNominationSummaries, setSortedAwardNominationSummaries] = useState<AwardNominationSummary[]>([]);
  const [selectedAwardNominationSummaries, setSelectedAwardNominationSummaries] = useState<AwardNominationSummary[]>([]);
  const [selectedAwardNominationIds, setSelectedAwardNominationIds] = useState<number[]>([]);
  const [selectedAwardNominationId, setSelectedAwardNominationId] = useState<number>(0);
  const [isShowDisqualifyScrubbingModal, setShowDisqualifyScrubbingModal] = useState(false);
  const [isShowPassScrubbingModal, setShowPassScrubbingModal] = useState(false);
  const [isShowExportNominationFieldsModal, setShowExportNominationFieldsModal] = useState<boolean>(false);
  const [isShowExportNominationsModal, setShowExportNominationsModal] = useState<boolean>(false);
  const [isAiScrubbingDialogOpen, setIsAiScrubbingDialogOpen] = useState<boolean>(false);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState<boolean>(false);
  const [reasoningDialogSummary, setReasoningDialogSummary] = useState<AwardNominationSummary | undefined>();
  const [scoringDialogSummary, setScoringDialogSummary] = useState<AwardNominationSummary | undefined>();
  const [overrideDialogSummary, setOverrideDialogSummary] = useState<AwardNominationSummary | undefined>();

  const [isShowExportNominationModal, setShowExportNominationModal] = useState<boolean>(false);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const focusableGroupAttr = useFocusableGroup({
    tabBehavior: 'limited-trap-focus',
  });

  const someNominationsHaveWinnerRole = useMemo(() => sortedAwardNominationSummaries.some((nomination) => (nomination.role === 'Winner' || nomination.role === 'Finalist')), [sortedAwardNominationSummaries]);
  const someNominationsHaveAssignedJudge = useMemo(() => sortedAwardNominationSummaries.some((nomination) => (nomination.judges?.length ?? 0) > 1), [sortedAwardNominationSummaries]);

  const isDownloadServiceInFlight = apiEndpointCallStatesStore.inFlightStatusMap.get('postResourceTicket') ?? false;
  const getAwardNominationSummaries = async (searchText?: string): Promise<void> => {
    try {
      const nominationRefs = await apiStore.getAwardNominationSummaries(instance, awardId, searchText);
      awardPageUiStore.setAwardNominationSummaries(nominationRefs);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const handleAiReviewClicked = async (): Promise<void> => {
    try {
      const scope = searchText
        ? await apiStore.getAwardNominationSummaries(instance, awardId, undefined)
        : sortedAwardNominationSummaries;
      if (hasPriorAgentScrub(scope)) {
        setShowOverwriteConfirm(true);
        return;
      }
    } catch {
      // fail open: open the dialog rather than block the primary action
    }
    setIsAiScrubbingDialogOpen(true);
  }

  const debouncedSearch = useDebouncedCallback(
    async (query: string) => {
      setIsLoading(true);
      try {
        await getAwardNominationSummaries(query);
      }
      finally {
        setIsLoading(false);
      }
    },
    1000
  );

  const handleSearchTextChanged = (value: string) => {
    setSearchText(value);
    debouncedSearch(value);
  }

  const handleNominationCheckedChange = (awardNominationSummary: AwardNominationSummary, selected: boolean) => {
    const nominationSummaries = [...sortedAwardNominationSummaries];
    nominationSummaries[nominationSummaries.indexOf(awardNominationSummary)].selected = selected;
    setSortedAwardNominationSummaries(nominationSummaries);
    nominationForStaffPageUiStore.setSortedAwardNominationSummaries(nominationSummaries);

    const selectedNominationSummaries = selectedAwardNominationSummaries;
    if (selected) {
      selectedNominationSummaries.push(awardNominationSummary);
    }
    else {
      selectedNominationSummaries.splice(selectedNominationSummaries.indexOf(awardNominationSummary), 1);
    }
    setSelectedAwardNominationSummaries(selectedNominationSummaries);
    setSelectedAwardNominationIds(selectedNominationSummaries.map((nomination) => nomination.nominationId));
  }

  const handleCancelExporting = () => {
    setShowExportNominationFieldsModal(false);
  }

  const handleExportNominations = async (selectedFields: string[]) => {
    await downloadAwardExportedNominationsSummaries(awardId, searchText, selectedFields).then(() => {
      setShowExportNominationFieldsModal(false);
    })
  }

  const handleExportNomination = async (exportedType: ExportType): Promise<void> => {
    if (selectedAwardNominationId > 0) {
      try {
        const resourceTicket = await apiStore.postResourceTicket(instance, {
          data: {
            routeData: {
              nominationId: selectedAwardNominationId,
            },
            queryString: {
              exportType: exportedType,
            },
          },
          resourceType: ResourceTypes.ExportedNomination,
        });
        downloadFile(resourceTicket.ticketId);
      } catch (error) {
        // TODO - handle error
      }
    }
    setShowExportNominationModal(false);
    setSelectedAwardNominationId(0);
  }

  const handleExportNominationsZip = async (exportedType: ExportType): Promise<void> => {
    if (selectedAwardNominationIds.length > 0) {
      try {
        const resourceTicket = await apiStore.postResourceTicket(instance, {
          data: {
            routeData: {
              awardId,
            },
            queryString: {
              nominationIds: selectedAwardNominationIds.join(','),
              exportType: exportedType,
            },
          },
          resourceType: ResourceTypes.ExportedAwardNominations,
        });
        downloadFile(resourceTicket.ticketId);
      } catch (error) {
        // TODO - handle error
      }
    } else {
      try {
        const resourceTicket = await apiStore.postResourceTicket(instance, {
          data: {
            routeData: {
              awardId,
            },
            queryString: {
              exportType: exportedType,
            },
          },
          resourceType: ResourceTypes.ExportedAwardNominations,
        });
        downloadFile(resourceTicket.ticketId);
      } catch (error) {
        // TODO - handle error
      }
    }
    setShowExportNominationsModal(false);
  };

  const updateAwardNominationScrubbingStatus = async (awardId: number, nominationId: number, scrubbingStatus: NominationScrubbingStatus): Promise<void> => {
    try {
      await apiStore.updateAwardNominationScrubbingStatus(instance, awardId, nominationId, scrubbingStatus);
    } catch (error) {
      // TODO - handle error
    }
  }

  const overrideAwardNominationScrubbing = async (awardId: number, nominationId: number, justification: string): Promise<void> => {
    await apiStore.overrideAwardNominationScrubbing(instance, awardId, nominationId, justification);
  }

  const getAwardStatistic = async (awardId: number): Promise<void> => {
    try {
      const awardStatistic = await apiStore.getAwardStatistic(instance, awardId);
      awardPageUiStore.setAwardStatistic(awardStatistic);
    } catch (error) {
      // TODO - handle error
    }
  }

  useAiScrubProgress({
    awardId,
    instance,
    store: awardPageUiStore,
    onTerminal: async () => {
      await getAwardStatistic(awardId);
      await getAwardNominationSummaries(searchText);
      awardPageUiStore.finalizeBatch();
    },
  });

  const refreshBatchStatus = async (batchRunId: string): Promise<void> => {
    try {
      const response = await apiStore.getAiScrubBatchStatus(instance, awardId, batchRunId);
      awardPageUiStore.setBatchProgress(response.run);
      awardPageUiStore.setQueueMetrics(response.queuePosition, response.queueDepth);
      awardPageUiStore.setIsEvaluationRun(response.isEvaluation);
    } catch {
      // best-effort refresh; leave the panel as-is on failure
    }
  };

  const handleCancelBatch = async (): Promise<void> => {
    const batchRunId = awardPageUiStore.activeBatchRunId;
    if (!batchRunId) return;
    try {
      await apiStore.cancelAiScrubBatch(instance, awardId, batchRunId);
      // 204: the existing progress pipeline (queue poll / SSE + finish()) drives the UI to Finalized.
    } catch (err) {
      const outcome = classifyCancelOutcome(cancelStatusCode(err));
      for (const cancelAction of cancelOutcomeActions(outcome)) {
        if (cancelAction === 'clearPersistedKey') clearPersistedBatchRunId(awardId, 'awards');
        else if (cancelAction === 'clearStore') awardPageUiStore.clearBatchProgress();
        else if (cancelAction === 'refreshStatus') await refreshBatchStatus(batchRunId);
      }
      const message = cancelOutcomeMessage(outcome);
      if (message) errorStore.reportValidationError(message);
    }
  };

  const renderBatchCompletionSummary = (): React.JSX.Element | null => {
    const summary = awardPageUiStore.lastBatchSummary;
    if (!summary) return null;
    const text = summary.isEvaluation
      ? `AI evaluation complete: ${summary.scrubbed} assessed, ${summary.unscrubbed} not assessed of ${summary.total}.`
      : `AI review complete: ${summary.scrubbed} scrubbed, ${summary.unscrubbed} unscrubbed of ${summary.total}.`;
    return (
      <MessageBar intent={summary.unscrubbed > 0 ? 'warning' : 'success'}>
        <MessageBarBody>{text}</MessageBarBody>
        <MessageBarActions>
          <Button appearance='transparent' onClick={() => awardPageUiStore.clearLastBatchSummary()}>Dismiss</Button>
        </MessageBarActions>
      </MessageBar>
    );
  };

  const handleExportDiagnostics = async () => {
    const batchRunId = awardPageUiStore.activeBatchRunId;
    if (!batchRunId) return;
    try {
      const diagnostics = await apiStore.getAiScrubDiagnosticsExport(instance, awardId, batchRunId);
      downloadJsonBlob(diagnostics, `ai-scrub-diagnostics-${batchRunId}.json`);
    } catch (error) {
      // Error surfaced via responseErrorHandler -> errorStore.
    }
  };

  const handlePassNominations = async () => {
    setIsLoading(true);

    const updatePromises = selectedAwardNominationSummaries.map(nomination =>
      updateAwardNominationScrubbingStatus(awardId, nomination.nominationId, {
        ...blankNominationScrubbingStatus(),
        nominationId: nomination.nominationId,
        scrubbingStatus: 'Passed'
      })
    );

    await Promise.allSettled(updatePromises);

    await getAwardStatistic(awardId);
    await getAwardNominationSummaries(searchText);
    setIsLoading(false);
    handleCancelScrubbing();
  }

  const handleCancelScrubbing = () => {
    sortedAwardNominationSummaries.forEach(summary => summary.selected = false);
    setShowDisqualifyScrubbingModal(false);
    setShowPassScrubbingModal(false);
    setSelectedAwardNominationSummaries([]);
    setSelectedAwardNominationIds([]);
  }

  const showArrow = (key: NominationSummarySortKey) => {
    if (showArrowOnKey !== key) return undefined;
    return (
      showArrowOnKey === lastSortedKey ?
        <>
          <ChevronDown12Regular />
        </> :
        <>
          <ChevronUp12Regular />
        </>
    )
  }

  const sortAwardNominationSummaryTable = (nominationSummaries: AwardNominationSummary[], key: NominationSummarySortKey) => {
    const isDescending = lastSortedKey === key;
    if (isDescending) {
      setLastSortedKey('');
    }
    else {
      setLastSortedKey(key);
    }
    setShowArrowOnKey(key);
    const sortedSummaries = sortAwardNominationSummariesByKey(nominationSummaries, key, isDescending);
    setSortedAwardNominationSummaries(sortedSummaries);
    nominationForStaffPageUiStore.setSortedAwardNominationSummaries(sortedSummaries);
  }

  const handleLoadAwardNominationSummaries = (): void => {
    if (searchText) {
      setSortedAwardNominationSummaries(awardPageUiStore.awardNominationSummaries);
      nominationForStaffPageUiStore.setSortedAwardNominationSummaries(awardPageUiStore.awardNominationSummaries);
      return;
    }
    if (awardStore.isReviewAndDesignateWindowOpen || awardStore.isComplianceAndRevisionsWindowOpen || awardStore.isWinnersWindowOpen) {
      const sortedSummaries = sortAwardNominationSummariesByKey(awardPageUiStore.awardNominationSummaries, 'score', true);
      setSortedAwardNominationSummaries(sortedSummaries);
      nominationForStaffPageUiStore.setSortedAwardNominationSummaries(sortedSummaries);
      return;
    }
    setSortedAwardNominationSummaries(awardPageUiStore.awardNominationSummaries);
    nominationForStaffPageUiStore.setSortedAwardNominationSummaries(awardPageUiStore.awardNominationSummaries);
  }

  const canPass = areAllSelectedPassEligible(selectedAwardNominationSummaries);

  const renderPassButton = (): React.JSX.Element => {
    const passButton = (
      <Button
        appearance='secondary'
        disabledFocusable={!canPass}
        onClick={() => { if (canPass) setShowPassScrubbingModal(true); }}
      >
        Pass
      </Button>
    );

    if (canPass) return passButton;

    return (
      <Tooltip content={PASS_INELIGIBLE_TOOLTIP} relationship='description' withArrow>
        {passButton}
      </Tooltip>
    );
  }

  const renderScrubbingStatusCells = (nominationSummary: AwardNominationSummary): React.JSX.Element => {
    const isAutoDisqualified = (nominationSummary.scrubbingStatusId === ScrubbingStatusId.Disqualified
      || nominationSummary.scrubbingStatusId === ScrubbingStatusId['Pending-Disqualified'])
      && (nominationSummary.scrubbingSourceCode === 'Rule' || nominationSummary.scrubbingSourceCode === 'LLM');

    return (
      <>
        <TableCell className='col-auto'>
          {
            isAutoDisqualified && (
              <Button
                appearance='transparent'
                icon={nominationSummary.scrubbingSourceCode === 'Rule' ? <GavelRegular /> : <SparkleRegular />}
                title={nominationSummary.scrubbingSourceCode === 'Rule' ? 'Disqualified by rule' : 'AI-recommended disqualification'}
                aria-label={nominationSummary.scrubbingSourceCode === 'Rule' ? 'View rule disqualification reasoning' : 'View AI disqualification reasoning'}
                onClick={() => setReasoningDialogSummary(nominationSummary)}
              />
            )
          }
        </TableCell>
        <TableCell className='col-status'>
          {scrubbingStateBadge(nominationSummary.scrubbingStatusId, nominationSummary.scrubbingStatusDisplayName)}
        </TableCell>
        <TableCell className='col-score'>
          {
            nominationSummary.aiOverallScore != null
              ? (
                <Button
                  appearance='transparent'
                  aria-label='View AI score breakdown'
                  onClick={() => setScoringDialogSummary(nominationSummary)}
                >
                  {nominationSummary.aiOverallScore.toFixed(2)}
                </Button>
              )
              : '-'
          }
        </TableCell>
        <TableCell className='reviewed-by-col'>
          {nominationSummary.scrubbedBy && nominationSummary.scrubbedBy}
        </TableCell>
        <TableCell className='assigned-since-col'>
          {nominationSummary.scrubbedByAssignedDate && format(utcToZonedTime(parseISO(nominationSummary.scrubbedByAssignedDate), 'America/Los_Angeles'), 'MM/dd/yyyy')}
        </TableCell>
      </>
    );
  }

  useEffect(() => {
    handleLoadAwardNominationSummaries();
  }, [awardPageUiStore.awardNominationSummaries]);

  useEffect(() => () => {
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  return (
    <div
      className='award-nomination-summary-table'
    >
      {
        isLoading && <Loading />
      }
      {
        isShowDisqualifyScrubbingModal &&
        <MultipleDisqualifyScrubbingModal
          onCancel={handleCancelScrubbing}
          onUpdateScrubbingStatus={async (nominationId, scrubbingStatus) => {
            await updateAwardNominationScrubbingStatus(awardId, nominationId, scrubbingStatus);
            await getAwardStatistic(awardId);
            await getAwardNominationSummaries(searchText);
          }}
          selectedNominationSummaries={selectedAwardNominationSummaries}
        />
      }
      {
        isShowPassScrubbingModal &&
        <PassScrubbingModal
          onCancelButtonClicked={handleCancelScrubbing}
          onSaveButtonClicked={handlePassNominations}
        />
      }
      {
        isShowExportNominationFieldsModal &&
        <ExportNominationFieldsModal
          isModalOpen={isShowExportNominationFieldsModal}
          isDownloading={isDownloadServiceInFlight}
          isCountryAward={false}
          onCancelButtonClicked={handleCancelExporting}
          onExportButtonClicked={handleExportNominations}
        />
      }
      {
        isShowExportNominationsModal &&
        <ExportNominationsModal
          isModalOpen={isShowExportNominationsModal}
          isDownloading={isDownloadServiceInFlight}
          onCancelButtonClicked={() => setShowExportNominationsModal(false)}
          onExportButtonClicked={handleExportNominationsZip}
        />
      }
      {
        isShowExportNominationModal &&
        <ExportNominationsModal
          isModalOpen={isShowExportNominationModal}
          isDownloading={isDownloadServiceInFlight}
          onCancelButtonClicked={() => {
            setShowExportNominationModal(false);
            setSelectedAwardNominationId(0);
          }}
          onExportButtonClicked={handleExportNomination}
        />
      }
      {showOverwriteConfirm && (
        <AiScrubOverwriteConfirmModal
          onCancel={() => setShowOverwriteConfirm(false)}
          onConfirm={() => { setShowOverwriteConfirm(false); setIsAiScrubbingDialogOpen(true); }}
        />
      )}
      <AiScrubbingTriggerDialog
        isOpen={isAiScrubbingDialogOpen}
        awardId={awardId}
        defaultScrubbingRubric={awardPageUiStore.selectedAward?.defaultScrubbingRubric}
        onClose={(batchRunId, isEvaluation) => {
          setIsAiScrubbingDialogOpen(false);
          if (batchRunId) {
            awardPageUiStore.setActiveBatchRunId(batchRunId);
            awardPageUiStore.setIsEvaluationRun(isEvaluation);
          }
        }}
      />
      <DisqualificationReasoningDialog
        isOpen={!!reasoningDialogSummary}
        nominationSummary={reasoningDialogSummary}
        loadAssessments={(nominationId) => apiStore.getAiAssessment(instance, awardId, nominationId)}
        onOverride={() => {
          setOverrideDialogSummary(reasoningDialogSummary);
          setReasoningDialogSummary(undefined);
        }}
        onClose={() => setReasoningDialogSummary(undefined)}
      />
      <ScoringReasoningDialog
        isOpen={!!scoringDialogSummary}
        nominationSummary={scoringDialogSummary}
        loadAssessments={(nominationId) => apiStore.getAiAssessment(instance, awardId, nominationId)}
        loadJudgingQuestions={() => apiStore.getStaffJudgingQuestions(instance, awardId)}
        loadHumanRatings={(nominationId) => apiStore.getHumanRatings(instance, awardId, nominationId)}
        onClose={() => setScoringDialogSummary(undefined)}
      />
      {
        overrideDialogSummary &&
        <OverrideScrubbingModal
          nominationTitle={overrideDialogSummary.title}
          onCancel={() => setOverrideDialogSummary(undefined)}
          onOverride={async (justification) => {
            await overrideAwardNominationScrubbing(awardId, overrideDialogSummary.nominationId, justification);
            setOverrideDialogSummary(undefined);
            await getAwardStatistic(awardId);
            await getAwardNominationSummaries(searchText);
          }}
        />
      }
      <FlexBox
        wrap
        isFullWidth
        aligns='center'
        content='space-between'
        className='award-nomination-summary-table__header'
      >
        <InfoLabel
          info={{
            children: getTooltipContentByFieldName('staff-award-page', 'searchNominationInput'),
            className: commonStyles.tooltip,
          }}
        >
          <SearchBox
            className={commonStyles.inputField}
            placeholder='Search'
            value={searchText}
            onChange={(_, data) => handleSearchTextChanged(data.value)}
          />
        </InfoLabel>

        <FlexBox
          className='header__buttons'
        >
          <FlexBox
            as='button'
            className='button-as-link'
            aligns='center'
            onClick={() => setShowExportNominationFieldsModal(true)}
          >
            <ArrowDownload16Regular />
            Export to Excel
          </FlexBox>
          <FlexBox
            as='button'
            className='button-as-link'
            aligns='center'
            onClick={() => setShowExportNominationsModal(true)}
          >
            <ArrowDownload16Regular />
            Download all nominations
          </FlexBox>
          <Button
            appearance='secondary'
            disabled={selectedAwardNominationIds.length === 0}
            onClick={() => setShowExportNominationsModal(true)}
          >
            Download
          </Button>
          {
            awardStore.isScrubbingWindowOpen &&
            <>
              {canStartAiScrub(userStore) && (
                <>
                  <Tooltip content={AI_SCRUB_OVERWRITE_WARNING} relationship='description' withArrow>
                    <Button
                      appearance='primary'
                      onClick={handleAiReviewClicked}
                      disabled={(apiEndpointCallStatesStore.inFlightStatusMap.get('startAiScrubBatch') ?? false) || awardPageUiStore.isBatchInProgress}
                    >
                      AI Review
                    </Button>
                  </Tooltip>
                  <BatchProgressIndicator store={awardPageUiStore} onCancel={handleCancelBatch} inFlightKey='cancelAiScrubBatch' />
                  {userStore.isAdmin && awardPageUiStore.activeBatchRunId && (
                    <Button
                      appearance='secondary'
                      onClick={handleExportDiagnostics}
                      disabled={apiEndpointCallStatesStore.inFlightStatusMap.get('getAiScrubDiagnosticsExport') ?? false}
                    >
                      Export diagnostics
                    </Button>
                  )}
                </>
              )}
              <Button
                appearance='secondary'
                disabled={!selectedAwardNominationSummaries.length}
                onClick={() => {
                  if (selectedAwardNominationSummaries.length > 0)
                    setShowDisqualifyScrubbingModal(true);
                }}
              >
                Disqualify
              </Button>
              {renderPassButton()}
            </>
          }
        </FlexBox>
      </FlexBox>

      {renderBatchCompletionSummary()}

      <div
        className='award-nomination-summary-table__table'
      >
        <Table>
          <TableHeader>
            <TableRow>
              {
                awardStore.isJudgingWindowOpen
                && someNominationsHaveAssignedJudge
                && <TableHeaderCell className='col-indicator' />
              }
              {
                someNominationsHaveWinnerRole
                && <TableHeaderCell className='col-indicator' />
              }
              <TableHeaderCell
                onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'title')}
                className={(awardStore.isNominationWindowOpen && awardStore.isScrubbingWindowOpen) ? 'col-nomination--short' : 'col-nomination'}
              >
                Nomination
                {showArrow('title')}
              </TableHeaderCell>
              {
                awardStore.isNominationWindowOpen &&
                <>
                  <TableHeaderCell
                    className='col-status'
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'status')}
                  >
                    Status
                    {showArrow('status')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'last-edited')}
                  >
                    Last Edited
                    {showArrow('last-edited')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'alias')}
                  >
                    Alias
                    {showArrow('alias')}
                  </TableHeaderCell>
                </>
              }
              {
                (awardStore.isReviewAndDesignateWindowOpen || awardStore.isComplianceAndRevisionsWindowOpen || awardStore.isWinnersWindowOpen) &&
                <>
                  <TableHeaderCell
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'company')}
                    className={awardStore.isWinnersWindowOpen ? 'autoSize-column' : ''}
                  >
                    Company
                    {showArrow('company')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'status')}
                    className={`col-status ${awardStore.isWinnersWindowOpen ? 'autoSize-column' : ''}`}
                  >
                    Status
                    {showArrow('status')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'score')}
                    className={awardStore.isWinnersWindowOpen ? 'autoSize-column' : ''}
                  >
                    Score
                    {showArrow('score')}
                  </TableHeaderCell>
                </>
              }
              {
                awardStore.isWinnersWindowOpen &&
                <>
                  <TableHeaderCell
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'questionnaire-status')}
                    className='autoSize-column'
                  >
                    Questionnaire
                    {showArrow('questionnaire-status')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'questionnaire-last-updated-date')}
                    className='autoSize-column'
                  >
                    Last Edited
                    {showArrow('questionnaire-last-updated-date')}
                  </TableHeaderCell>
                </>
              }
              {
                awardStore.isScrubbingWindowOpen &&
                <>
                  <TableHeaderCell
                    className='col-auto'
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'scrubbing-source')}
                  >
                    Auto
                    {showArrow('scrubbing-source')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    className='col-status'
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'scrubbing-status')}
                  >
                    Scrubbing status
                    {showArrow('scrubbing-status')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    className='col-score'
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'ai-score')}
                  >
                    Score
                    {showArrow('ai-score')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    className='reviewed-by-col'
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'scrubbing-by')}
                  >
                    Reviewed By
                    {showArrow('scrubbing-by')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    className='assigned-since-col'
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'scrubbing-by-assigned-date')}
                  >
                    Assigned Since
                    {showArrow('scrubbing-by-assigned-date')}
                  </TableHeaderCell>
                </>
              }
              {
                awardStore.isJudgingWindowOpen &&
                <>
                  <TableHeaderCell
                    className='col-status'
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'status')}
                  >
                    Status
                    {showArrow('status')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'assigned-to')}
                  >
                    Assigned to
                    {showArrow('assigned-to')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'assigned-date')}
                  >
                    Assigned since
                    {showArrow('assigned-date')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => sortAwardNominationSummaryTable(sortedAwardNominationSummaries, 'judged')}
                  >
                    Judged
                    {showArrow('judged')}
                  </TableHeaderCell>
                </>
              }
              <TableHeaderCell className='col-actions' />
            </TableRow>
          </TableHeader>
          <TableBody>
            {
              sortedAwardNominationSummaries.map((nominationSummary) => {
                const isRuleDisqualified = (nominationSummary.scrubbingStatusId === ScrubbingStatusId.Disqualified
                  || nominationSummary.scrubbingStatusId === ScrubbingStatusId['Pending-Disqualified'])
                  && nominationSummary.scrubbingSourceCode === 'Rule';
                const isAgentDisqualified = nominationSummary.scrubbingStatusId === ScrubbingStatusId['Pending-Disqualified']
                  && nominationSummary.scrubbingSourceCode === 'LLM';
                return (
                  <Fragment
                    key={nominationSummary.nominationId}
                  >
                    <TableRow className={isRuleDisqualified ? 'row--rule-disqualified' : isAgentDisqualified ? 'row--recommend-disqualified' : undefined}>
                      {
                        (awardStore.isJudgingWindowOpen && someNominationsHaveAssignedJudge) &&
                        <TableCell className='col-indicator'>
                          <Button
                            appearance='transparent'
                            title='expand'
                            icon={expandedRows.indexOf(nominationSummary.nominationId) > -1 ? <ChevronDown12Regular /> : <ChevronUp12Regular />}
                            className='expanded-rows-icon'
                            onClick={() => {
                              if (expandedRows.indexOf(nominationSummary.nominationId) > -1) {
                                expandedRows.splice(expandedRows.indexOf(nominationSummary.nominationId), 1);
                              } else {
                                expandedRows.push(nominationSummary.nominationId);
                              }

                              setExpandedRows([...expandedRows]);
                            }}
                          />
                        </TableCell>
                      }
                      {
                        someNominationsHaveWinnerRole &&
                        <TableCell className={`col-indicator col-indicator--${nominationSummary.role?.toLowerCase()}`}>
                          <StarFilled
                            title={nominationSummary.role}
                            color={nominationSummary.role === 'Winner' ? '#FFB900' : '#BDD8E3'}
                          />
                        </TableCell>
                      }
                      <TableCell className='col-nomination'>
                        <NavLink
                          to={RoutePaths.Awards + '/' + awardId + RoutePaths.Nominations + '/' + nominationSummary.nominationId}
                          className={({ isActive, isPending }) =>
                            isPending ? 'pending' : isActive ? 'active' : ''
                          }
                          end
                        >
                          {nominationSummary.title}
                        </NavLink>
                      </TableCell>
                      {
                        awardStore.isNominationWindowOpen &&
                        <>
                          <TableCell
                            className={`col-status ${nominationSummary.statusDisplayName === 'Incomplete' ? 'text--orange' : ''}`}
                          >
                            {nominationSummary.statusDisplayName}
                          </TableCell>
                          <TableCell>
                            {nominationSummary.lastEditedDate ? format(utcToZonedTime(parseISO(nominationSummary.lastEditedDate!), 'America/Los_Angeles'), 'MM/dd/yyyy') : ''}
                          </TableCell>
                          <TableCell>
                            {nominationSummary.alias}
                          </TableCell>
                        </>
                      }
                      {
                        (awardStore.isReviewAndDesignateWindowOpen || awardStore.isComplianceAndRevisionsWindowOpen || awardStore.isWinnersWindowOpen) &&
                        <>
                          <TableCell>{awardStore.isWinnersWindowOpen && nominationSummary.questionnaireSummaries[0].companyName ? nominationSummary.questionnaireSummaries[0].companyName : nominationSummary.company ?? ''}</TableCell>
                          <TableCell
                            className={!nominationSummary.scrubbingStatusDisplayName && nominationSummary.statusDisplayName === 'Incomplete' ? 'text--orange' : ''}
                          >
                            {nominationSummary.scrubbingStatusDisplayName ? nominationSummary.scrubbingStatusDisplayName : nominationSummary.statusDisplayName}
                          </TableCell>
                          <TableCell>{nominationSummary.score ?? 0}</TableCell>
                        </>
                      }
                      {
                        awardStore.isWinnersWindowOpen &&
                        <>
                          <TableCell className={`col-status ${scrubbingStatusClassNameColumn(nominationSummary.scrubbingStatusDisplayName || 'Incomplete')}`}>
                            {
                              nominationSummary.questionnaireSummaries && nominationSummary.questionnaireSummaries.length > 0 &&
                              nominationSummary.questionnaireSummaries[0].status
                            }
                          </TableCell>
                          <TableCell>
                            {
                              nominationSummary.questionnaireSummaries && nominationSummary.questionnaireSummaries.length > 0 &&
                              nominationSummary.questionnaireSummaries[0].lastUpdatedDate && format(utcToZonedTime(parseISO(nominationSummary.questionnaireSummaries[0].lastUpdatedDate), 'America/Los_Angeles'), 'MM/dd/yyyy')
                            }
                          </TableCell>
                        </>
                      }
                      {
                        awardStore.isScrubbingWindowOpen &&
                        renderScrubbingStatusCells(nominationSummary)
                      }
                      {
                        awardStore.isJudgingWindowOpen &&
                        <>
                          <TableCell
                            className={`col-status ${scrubbingStatusClassNameColumn(nominationSummary.scrubbingStatusDisplayName || 'Incomplete')}`}
                          >
                            {nominationSummary.scrubbingStatusDisplayName ? nominationSummary.scrubbingStatusDisplayName : nominationSummary.statusDisplayName}
                          </TableCell>
                          <TableCell>
                            {
                              (nominationSummary.judges && nominationSummary.judges.length) > 0 ?
                                getUserNames([nominationSummary.judges[0].user]) + (nominationSummary.judges.length > 1 && expandedRows.indexOf(nominationSummary.nominationId) === -1 ?
                                  ' +' + (nominationSummary.judges.length - 1) :
                                  '') :
                                ''
                            }
                          </TableCell>
                          <TableCell>
                            {
                              (nominationSummary.judges && nominationSummary.judges.length) > 0 ? format(utcToZonedTime(parseISO(nominationSummary.judges[0].assignmentDate), 'America/Los_Angeles'), 'MM/dd/yyyy') : ''
                            }
                          </TableCell>
                          <TableCell>{nominationSummary.numberOfCompletedJudgment}</TableCell>
                        </>
                      }
                      <TableCell
                        tabIndex={0}
                        className='col-actions'
                        {...focusableGroupAttr}
                      >
                        <FlexBox
                          content='end'
                        >
                          <Button
                            title='Download'
                            appearance='transparent'
                            icon={<ArrowDownloadRegular />}
                            onClick={() => {
                              setSelectedAwardNominationId(nominationSummary.nominationId);
                              setShowExportNominationModal(true);
                            }}
                          />
                          {
                            canAssignWinnersAndFinalists && (
                              <Radio
                                aria-label='select award nomination'
                                title='select award nomination'
                                id={`table-checked-${nominationSummary.nominationId}`}
                                checked={awardPageUiStore.selectedAwardNominationSummary?.nominationId === nominationSummary.nominationId}
                                onChange={() => setSelectedAwardNominationSummary(nominationSummary)}
                              />
                            )
                          }
                          <Checkbox
                            aria-label='select nomination'
                            title={
                              isRuleDisqualified
                                ? 'Disqualified by rule - cannot be passed'
                                : isAgentDisqualified
                                  ? 'AI recommends disqualification - review before passing'
                                  : 'select nomination'
                            }
                            disabled={isRuleDisqualified}
                            checked={!isRuleDisqualified && (nominationSummary.selected ?? false)}
                            onChange={(_, data) => handleNominationCheckedChange(nominationSummary, data.checked as boolean)}
                          />

                        </FlexBox>
                      </TableCell>
                    </TableRow>
                    {
                      awardStore.isJudgingWindowOpen &&
                      expandedRows.indexOf(nominationSummary.nominationId) > -1 &&
                      nominationSummary.judges
                        .filter(value => value.user.emailAddress !== nominationSummary.judges[0].user.emailAddress).map(judge => {
                          return (
                            <TableRow
                              key={judge.user.userId}
                            >
                              <TableCell className='col-indicator' />
                              <TableCell />
                              <TableCell />
                              <TableCell>{getUserNames([judge.user])}</TableCell>
                              <TableCell>{format(utcToZonedTime(parseISO(judge.assignmentDate), 'America/Los_Angeles'), 'MM/dd/yyyy')}</TableCell>
                              <TableCell />
                              <TableCell />
                            </TableRow>);
                        })
                    }
                    {
                      awardStore.isWinnersWindowOpen &&
                      nominationSummary.questionnaireSummaries
                        .filter(value => value.companyName !== nominationSummary.questionnaireSummaries[0].companyName)
                        .map(value => {
                          return (
                            <TableRow
                              key={value.nominationId}
                            >
                              <TableCell className='col-indicator' />
                              <TableCell />
                              <TableCell>{value.companyName}</TableCell>
                              <TableCell />
                              <TableCell />
                              <TableCell className={`col-status ${scrubbingStatusClassNameColumn(nominationSummary.scrubbingStatusDisplayName || 'Incomplete')}`}>{value.status}</TableCell>
                              <TableCell>{value.lastUpdatedDate ? format(utcToZonedTime(parseISO(value.lastUpdatedDate), 'America/Los_Angeles'), 'MM/dd/yyyy') : ''}</TableCell>
                            </TableRow>);
                        })
                    }
                  </Fragment>
                );
              })
            }
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


export const AwardNominationSummaryTable = observer(AwardNominationSummaryTableImpl);
